import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pencil, Trash2, Plus, LogOut, Eye, ExternalLink, MessageCircle, Send, Sparkles, RefreshCw } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { buildWhatsAppLink, WHATSAPP_NUMBER } from "../../lib/whatsapp";

const STATUS_FLOW = ["new", "quoted", "paid", "shipped", "delivered", "cancelled"];

const STATUS_STYLES = {
  new:       { bg: "bg-[#E5E0D8]",           text: "text-[#1A1A1A]", label: "New" },
  quoted:    { bg: "bg-[#C9A96E]/25",        text: "text-[#8B6E3A]", label: "Quoted" },
  paid:      { bg: "bg-[#C9A96E]",           text: "text-[#0A0A0A]", label: "Paid" },
  shipped:   { bg: "bg-[#0A0A0A]",           text: "text-[#C9A96E]", label: "Shipped" },
  delivered: { bg: "bg-emerald-700",         text: "text-white",     label: "Delivered" },
  cancelled: { bg: "bg-red-800/80",          text: "text-white",     label: "Cancelled" },
};

function daysBetween(then, now = new Date()) {
  if (!then) return null;
  const d = new Date(then);
  if (isNaN(d)) return null;
  const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  return diff;
}

// Normalise a client-supplied WhatsApp / phone number to an E.164-ish string
// suitable for wa.me — keeps digits only, drops "+", spaces, dashes, parens.
function normalisePhone(raw) {
  if (!raw) return "";
  return String(raw).replace(/[^0-9]/g, "");
}

function buildClientReplyMessage(order) {
  const firstName = (order.client_name || "").split(" ")[0] || "there";
  return (
    `Hi ${firstName}, thanks for placing order ${order.ref} with My Private Shopper.\n` +
    `I've received your details and I'm working on your confirmed quote now.\n` +
    `I'll be back to you within a few hours.\n\n` +
    `— MPS Concierge`
  );
}

function buildDigestMessage(orders) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todays = orders.filter((o) => {
    const d = new Date(o.created_at);
    return !isNaN(d) && d >= today;
  });
  const dateLabel = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  if (todays.length === 0) {
    return `MPS · Daily Digest — ${dateLabel}\n\nNo new orders today.`;
  }

  const totalValue = todays.reduce((s, o) => s + Number(o.estimated_total || 0), 0);
  const lines = todays.map(
    (o) =>
      `• ${o.ref} — ${o.client_name} — ${o.destination_state} — £${Number(o.estimated_total || 0).toFixed(2)}`,
  );
  return (
    `MPS · Daily Digest — ${dateLabel}\n\n` +
    `${todays.length} new order${todays.length === 1 ? "" : "s"} · Est. total £${totalValue.toFixed(2)}\n\n` +
    lines.join("\n")
  );
}

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("posts");
  const [loading, setLoading] = useState(true);
  const [digest, setDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, o, d] = await Promise.all([
        api.adminListPosts(),
        api.adminListOrders(),
        api.adminGetTodaysDigest().catch(() => null),
      ]);
      setPosts(p);
      setOrders(o);
      setDigest(d);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    await api.adminDeletePost(slug);
    load();
  };

  const onStatusCycle = async (order) => {
    const current = order.status || "new";
    const idx = STATUS_FLOW.indexOf(current);
    const next = STATUS_FLOW[(idx + 1) % STATUS_FLOW.length];
    try {
      await api.adminUpdateOrderStatus(order.ref, next);
      setOrders((os) =>
        os.map((o) =>
          o.ref === order.ref
            ? { ...o, status: next, status_updated_at: new Date().toISOString() }
            : o,
        ),
      );
    } catch (e) {
      console.error(e);
      alert("Failed to update status");
    }
  };

  const sendDigest = async () => {
    if (!digest) return;
    setDigestBusy(true);
    try {
      const digitsOnly = WHATSAPP_NUMBER.replace(/[^0-9]/g, "");
      const url = `https://wa.me/${digitsOnly}?text=${encodeURIComponent(digest.text)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      await api.adminMarkDigestSent(digest.date);
      setDigest({ ...digest, sent: true });
    } catch (e) {
      console.error(e);
    } finally {
      setDigestBusy(false);
    }
  };

  const regenerateDigest = async () => {
    setDigestBusy(true);
    try {
      const d = await api.adminRegenerateDigest();
      setDigest(d);
    } catch (e) {
      console.error(e);
    } finally {
      setDigestBusy(false);
    }
  };

  const liveDigestUrl = useMemo(() => {
    const digitsOnly = WHATSAPP_NUMBER.replace(/[^0-9]/g, "");
    const msg = buildDigestMessage(orders);
    return `https://wa.me/${digitsOnly}?text=${encodeURIComponent(msg)}`;
  }, [orders]);

  const todaysCount = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return orders.filter((o) => {
      const d = new Date(o.created_at);
      return !isNaN(d) && d >= t;
    }).length;
  }, [orders]);

  return (
    <div className="min-h-screen bg-[#F8F5F0] text-[#1A1A1A]">
      <header className="bg-[#0A0A0A] text-[#F8F5F0]">
        <div className="container-mps flex items-center justify-between h-20">
          <div className="flex items-center gap-6">
            <span className="font-serif text-2xl text-[#C9A96E]">MPS Admin</span>
            <span className="overline text-[#F8F5F0]/60">{user?.email}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="text-xs uppercase tracking-[0.18em] text-[#F8F5F0]/70 hover:text-[#C9A96E] inline-flex items-center gap-1"
            >
              View Site <ExternalLink size={12} />
            </Link>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A96E]/40 text-xs uppercase tracking-[0.16em] hover:bg-[#C9A96E] hover:text-[#0A0A0A]"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-mps py-12">
        <div className="flex border-b border-[#E5E0D8] mb-10">
          {["posts", "orders"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-6 py-3 text-sm uppercase tracking-[0.16em] -mb-px border-b-2 ${
                tab === t ? "border-[#C9A96E] text-[#1A1A1A]" : "border-transparent text-[#6B7280]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "posts" && (
          <>
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-serif text-3xl md:text-4xl">Blog Posts</h1>
              <Link
                to="/admin/posts/new"
                data-testid="admin-new-post"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#0A0A0A] text-[#F8F5F0] text-sm uppercase tracking-[0.16em] hover:bg-[#1A1A1A]"
              >
                <Plus size={16} /> New Post
              </Link>
            </div>

            {loading ? (
              <div className="text-[#6B7280]">Loading…</div>
            ) : (
              <div className="bg-white border border-[#E5E0D8]">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E5E0D8] overline text-[#6B7280]">
                      <th className="text-left p-4">Title</th>
                      <th className="text-left p-4">Category</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-right p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((p) => (
                      <tr key={p.slug} className="border-b border-[#E5E0D8] last:border-b-0">
                        <td className="p-4">
                          <div className="font-serif text-lg">{p.title}</div>
                          <div className="text-xs text-[#6B7280]">/{p.slug}</div>
                        </td>
                        <td className="p-4 text-sm">{p.category}</td>
                        <td className="p-4 text-sm">
                          <span
                            className={`px-2 py-1 text-xs uppercase tracking-wide ${
                              p.published ? "bg-[#C9A96E]/15 text-[#C9A96E]" : "bg-[#E5E0D8] text-[#6B7280]"
                            }`}
                          >
                            {p.published ? "Published" : "Draft"}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="inline-flex gap-3 justify-end">
                            <Link
                              to={`/blog/${p.slug}`}
                              target="_blank"
                              className="p-2 hover:bg-[#E5E0D8]"
                              data-testid={`admin-post-view-${p.slug}`}
                            >
                              <Eye size={16} />
                            </Link>
                            <Link
                              to={`/admin/posts/${p.slug}`}
                              data-testid={`admin-post-edit-${p.slug}`}
                              className="p-2 hover:bg-[#E5E0D8]"
                            >
                              <Pencil size={16} />
                            </Link>
                            <button
                              data-testid={`admin-post-delete-${p.slug}`}
                              onClick={() => onDelete(p.slug)}
                              className="p-2 hover:bg-[#E5E0D8] text-[#1A1A1A]"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "orders" && (
          <>
            {/* Auto-prepared daily digest banner */}
            {digest ? (
              <div
                data-testid="admin-digest-banner"
                className={`mb-8 p-6 border ${
                  digest.sent
                    ? "bg-[#F8F5F0] border-[#E5E0D8]"
                    : "bg-[#0A0A0A] text-[#F8F5F0] border-[#C9A96E]"
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 justify-between">
                  <div className="flex items-start gap-4">
                    <Sparkles
                      className={digest.sent ? "text-[#6B7280]" : "text-[#C9A96E]"}
                      size={22}
                      strokeWidth={1.4}
                    />
                    <div>
                      <div
                        className={`overline mb-1 ${
                          digest.sent ? "text-[#6B7280]" : "text-[#C9A96E]"
                        }`}
                      >
                        {digest.sent ? "Digest already sent" : "Today's digest is ready"}
                      </div>
                      <div className="font-serif text-lg md:text-xl">
                        {digest.orders_count} order{digest.orders_count === 1 ? "" : "s"} · £
                        {Number(digest.total_value || 0).toFixed(2)} estimated
                      </div>
                      <div className="text-xs opacity-70 mt-1">
                        Auto-generated · Runs 20:00 Europe/London
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 shrink-0">
                    <button
                      data-testid="admin-digest-regenerate"
                      onClick={regenerateDigest}
                      disabled={digestBusy}
                      className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.16em] border ${
                        digest.sent
                          ? "border-[#E5E0D8] text-[#1A1A1A] hover:bg-[#E5E0D8]"
                          : "border-[#C9A96E]/40 text-[#F8F5F0] hover:bg-[#C9A96E] hover:text-[#0A0A0A]"
                      } disabled:opacity-50`}
                    >
                      <RefreshCw size={13} /> Regenerate
                    </button>
                    <button
                      data-testid="admin-digest-send"
                      onClick={sendDigest}
                      disabled={digestBusy}
                      className="inline-flex items-center gap-2 px-5 py-2 bg-[#25D366] text-[#0A0A0A] text-xs uppercase tracking-[0.16em] hover:bg-[#1EBE5C] disabled:opacity-50"
                    >
                      <Send size={13} /> {digest.sent ? "Send Again" : "Send on WhatsApp"}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                data-testid="admin-digest-banner-empty"
                className="mb-8 p-6 border border-[#E5E0D8] bg-[#F8F5F0]"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-sm text-[#6B7280]">
                    <Sparkles size={18} strokeWidth={1.4} />
                    <span>No digest prepared yet — auto-generates at 20:00 Europe/London.</span>
                  </div>
                  <button
                    onClick={regenerateDigest}
                    disabled={digestBusy}
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.16em] border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#0A0A0A] hover:text-[#F8F5F0] disabled:opacity-50"
                  >
                    <RefreshCw size={13} /> Generate Now
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <h1 className="font-serif text-3xl md:text-4xl">Recent Orders</h1>
              <a
                href={liveDigestUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-send-digest-live"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#1A1A1A] text-[#1A1A1A] text-xs uppercase tracking-[0.16em] hover:bg-[#0A0A0A] hover:text-[#F8F5F0]"
                title="Send an ad-hoc live digest of today's orders now"
              >
                <Send size={14} /> Live Digest
                <span className="ml-1 px-2 py-0.5 bg-[#0A0A0A] text-[#C9A96E] text-[10px] rounded-full group-hover:bg-[#F8F5F0]">
                  {todaysCount}
                </span>
              </a>
            </div>
            {loading ? (
              <div className="text-[#6B7280]">Loading…</div>
            ) : orders.length === 0 ? (
              <div className="text-[#6B7280]">No orders yet.</div>
            ) : (
              <div className="bg-white border border-[#E5E0D8] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E0D8] overline text-[#6B7280]">
                      <th className="text-left p-4">Ref</th>
                      <th className="text-left p-4">Client</th>
                      <th className="text-left p-4">State</th>
                      <th className="text-left p-4">Total</th>
                      <th className="text-left p-4">Date</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-right p-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => {
                      const digits = normalisePhone(o.whatsapp);
                      const replyUrl = digits
                        ? `https://wa.me/${digits}?text=${encodeURIComponent(buildClientReplyMessage(o))}`
                        : null;
                      const status = o.status || "new";
                      const style = STATUS_STYLES[status] || STATUS_STYLES.new;
                      const days = daysBetween(o.status_updated_at || o.created_at);
                      return (
                        <tr key={o.ref} className="border-b border-[#E5E0D8] last:border-b-0">
                          <td className="p-4 font-medium">{o.ref}</td>
                          <td className="p-4">
                            <div>{o.client_name}</div>
                            <div className="text-xs text-[#6B7280]">{o.whatsapp}</div>
                          </td>
                          <td className="p-4">{o.destination_state}</td>
                          <td className="p-4">£{Number(o.estimated_total).toFixed(2)}</td>
                          <td className="p-4">{new Date(o.created_at).toLocaleDateString("en-GB")}</td>
                          <td className="p-4">
                            <button
                              data-testid={`admin-order-status-${o.ref}`}
                              onClick={() => onStatusCycle(o)}
                              title="Click to move to next stage"
                              className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] ${style.bg} ${style.text} hover:opacity-90`}
                            >
                              {style.label}
                              {days != null && (
                                <span className="opacity-75 text-[10px]">
                                  · {days}d
                                </span>
                              )}
                            </button>
                          </td>
                          <td className="p-4 text-right">
                            {replyUrl ? (
                              <a
                                href={replyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`admin-order-reply-${o.ref}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] text-[#0A0A0A] text-[11px] uppercase tracking-[0.14em] hover:bg-[#1EBE5C]"
                                title="Open WhatsApp to reply to this client"
                              >
                                <MessageCircle size={13} /> Reply
                              </a>
                            ) : (
                              <span className="text-xs text-[#6B7280]">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
